import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import axios from 'axios';

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const getLocalDateString = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Active, 2=Expired

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_amount: '',
    maximum_discount: '',
    usage_limit: '',
    is_active: true,
    valid_from: '',
    valid_until: '',
    applicable_events: null,
  });

  useEffect(() => {
    loadPromoCodes();
    testDatabaseConnection();
  }, []);

  const testDatabaseConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/promo_codes/test.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
    }
  };

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/promo_codes/list.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      if (response.data.success) {
        setCodes(response.data.data);
      } else {
        setError(response.data.message || 'Fehler beim Laden der Promo-Codes');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Promo-Codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditingCode(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      minimum_amount: '',
      maximum_discount: '',
      usage_limit: '',
      is_active: true,
      valid_from: getLocalDateString(),
      valid_until: getLocalDateString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
      applicable_events: null,
    });
    setDialogOpen(true);
  };

  const handleEditClick = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description,
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      minimum_amount: code.minimum_amount || '',
      maximum_discount: code.maximum_discount || '',
      usage_limit: code.usage_limit || '',
      is_active: code.is_active,
      valid_from: code.valid_from,
      valid_until: code.valid_until,
      applicable_events: code.applicable_events,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Validate required fields
      if (!formData.code || !formData.discount_value || !formData.valid_from || !formData.valid_until) {
      
        setError('Bitte füllen Sie alle Pflichtfelder aus');
        return;
      }

      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        minimum_amount: formData.minimum_amount ? parseFloat(formData.minimum_amount) : 0,
        maximum_discount: formData.maximum_discount ? parseFloat(formData.maximum_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      };

      let response;
      if (editingCode) {
        response = await axios.post(`${API_BASE_URL}/promo_codes/update.php`, 
          { id: editingCode.id, ...payload },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/promo_codes/create.php`, 
          payload,
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }

      if (response.data.success) {
        setDialogOpen(false);
        loadPromoCodes();
        setError('');
      } else {
        setError(response.data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async (code) => {
    if (window.confirm(`Möchten Sie den Promo-Code "${code.code}" wirklich löschen?`)) {
      try {
        setSaving(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_BASE_URL}/promo_codes/delete.php`, 
          { id: code.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );

        if (response.data.success) {
          loadPromoCodes();
          setError('');
        } else {
          setError(response.data.message || 'Fehler beim Löschen');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Fehler beim Löschen');
      } finally {
        setSaving(false);
      }
    }
  };

  // Filter codes based on tab
  const getFilteredCodes = () => {
    switch (tabValue) {
      case 1: // Active
        return codes.filter(code => code.is_active && !code.is_expired && !code.is_usage_limit_reached);
      case 2: // Expired
        return codes.filter(code => code.is_expired || code.is_usage_limit_reached);
      default: // All
        return codes;
    }
  };

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      fontWeight: 600,
    },
    {
      field: 'description',
      headerName: 'Beschreibung',
      width: 200,
    },
    {
      field: 'discount',
      headerName: 'Rabatt',
      width: 120,
      renderCell: (params) => {
        const { discount_type, discount_value } = params.row;
        return discount_type === 'percentage' 
          ? `${discount_value}%` 
          : `CHF ${discount_value}`;
      },
    },
    {
      field: 'valid_dates',
      headerName: 'Gültigkeitszeitraum',
      width: 180,
      renderCell: (params) => {
        const { valid_from, valid_until, is_expired } = params.row;
        return (
          <Box>
            <Typography variant="caption" display="block">
              {new Date(valid_from).toLocaleDateString('de-DE')} - {new Date(valid_until).toLocaleDateString('de-DE')}
            </Typography>
            {is_expired && (
              <Chip label="Abgelaufen" size="small" color="error" />
            )}
          </Box>
        );
      },
    },
    {
      field: 'usage',
      headerName: 'Nutzung',
      width: 120,
      renderCell: (params) => {
        const { used_count, usage_limit, usage_percentage } = params.row;
        if (usage_limit) {
          return (
            <Box>
              <Typography variant="body2">
                {used_count}/{usage_limit}
              </Typography>
              {usage_percentage && (
                <Typography variant="caption" color="text.secondary">
                  ({usage_percentage}%)
                </Typography>
              )}
            </Box>
          );
        }
        return used_count || 0;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const { is_active, is_expired, is_usage_limit_reached } = params.row;
        
        if (is_expired) {
          return <Chip label="Abgelaufen" color="error" size="small" />;
        }
        if (is_usage_limit_reached) {
          return <Chip label="Limit erreicht" color="warning" size="small" />;
        }
        if (!is_active) {
          return <Chip label="Inaktiv" color="default" size="small" />;
        }
        return <Chip label="Aktiv" color="success" size="small" />;
      },
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            color="info"
            onClick={() => handleEditClick(params.row)}
            size="small"
            title="Bearbeiten"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row)}
            size="small"
            title="Löschen"
            disabled={params.row.used_count > 0}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          Promo-Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{ px: 3 }}
        >
          Neuer Promo-Code
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Alle (${codes.length})`} />
          <Tab label={`Aktiv (${codes.filter(c => c.is_active && !c.is_expired && !c.is_usage_limit_reached).length})`} />
          <Tab label={`Abgelaufen (${codes.filter(c => c.is_expired || c.is_usage_limit_reached).length})`} />
        </Tabs>
        
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={getFilteredCodes()}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={loading}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCode ? 'Promo-Code Bearbeiten' : 'Neuer Promo-Code'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="z.B. WELCOME10"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. Willkommensrabatt"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rabatt-Typ</InputLabel>
                <Select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                >
                  <MenuItem value="percentage">Prozent</MenuItem>
                  <MenuItem value="fixed">Fester Betrag</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rabatt-Wert"
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.discount_type === 'percentage' ? '%' : 'CHF'}
                    </InputAdornment>
                  ),
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mindestbetrag"
                type="number"
                value={formData.minimum_amount}
                onChange={(e) => setFormData({ ...formData, minimum_amount: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">CHF</InputAdornment>,
                }}
                placeholder="0"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximaler Rabatt"
                type="number"
                value={formData.maximum_discount}
                onChange={(e) => setFormData({ ...formData, maximum_discount: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">CHF</InputAdornment>,
                }}
                placeholder="Unbegrenzt"
                disabled={formData.discount_type === 'fixed'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nutzungslimit"
                type="number"
                value={formData.usage_limit}
                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                placeholder="Unbegrenzt"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Aktiv"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gültig ab"
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gültig bis"
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving || !formData.code || !formData.discount_value}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoCodes;
